import React, { useEffect, useState } from 'react';
import { Form, useNavigate, NavLink } from 'react-router-dom';
import { IVendor } from '../../types/vendor';
import moment from 'moment';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import Json from '../json';

import { IShipmentLight } from '../../types/shipment';
import { IVendorTransporterValueSets } from '../../types/vendor';

import { Icons } from '../icons';
import Spinner from '../spinner';
import { useUserContext } from '../../contexts/userContext';
import { ErrorProvider } from '../../classes/errorProvider';

import { FormatService } from '../../services/formatService';
import { ShipmentService } from '../../services/shipmentService';
import { UtilityService } from '../../services/utilityService';
import WorkingModal, { IWorkingModalProps } from '../workingModal';

import '../../styles/shipmentDetailsForm.scss';

export interface IShipmentDetailsForm {
	shipmentDetails: IShipmentLight;
	vendor: IVendor;
	isLoadingDetails: boolean;
	vendorTransporterValueSets: IVendorTransporterValueSets;
	isLoadingVendorsAndTransporters?: boolean;
	isLoadingShipmentManifestWarnings: boolean;
}

const ShipmentDetailsForm: React.FunctionComponent<IShipmentDetailsForm> = ({ shipmentDetails, vendor, isLoadingDetails, vendorTransporterValueSets, isLoadingVendorsAndTransporters, isLoadingShipmentManifestWarnings }) => {
	const userContext = useUserContext();
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [errorOnDelete, setErrorOnDelete] = useState<string | undefined>(undefined);
	const [deleteReason, setDeleteReason] = useState<string | undefined>(undefined);
	const [warningMessage, setWarningMessage] = useState<string | undefined>(undefined);
	const [disableWorkflowButtons, setDisableWorkflowButtons] = useState(false);
	const [showRealDeleteButton, setShowRealDeleteButton] = useState(false);
	const [pickupDate, setPickupDate] = useState(!isNaN(shipmentDetails.pickupDate.getTime()) ? (moment(shipmentDetails.pickupDate.toString())).format('MM/DD/yyyy') : "");
	const [showDateField, setShowDateField] = useState(false);
	const [dateField, setDateField] = useState(shipmentDetails.pickupDate ? shipmentDetails.pickupDate : dayjs());
	const [showBolField, setShowBolField] = useState(false);
	const [showVendorField, setShowVendorField] = useState(false);
	const [showTransporterField, setShowTransporterField] = useState(false);
	const [bolField, setBolField] = useState(shipmentDetails.hwiNumber ? shipmentDetails.hwiNumber : "");
	const [selectedVendorId, setSelectedVendorId] = useState<string>(shipmentDetails.vendorId ? shipmentDetails.vendorId : "");
	const [selectedTransporterId, setSelectedTransporterId] = useState<string>(shipmentDetails.transporterId ? shipmentDetails.transporterId : "");
	const [isSavingVendorAndTransporter, setIsSavingVendorAndTransporter] = useState(false);
	const [earliestAllowedDate, setEarliestAllowedDate] = useState<Dayjs>(dayjs("2025-4-01"));
	const [showAddFlagDialog, setShowAddFlagDialog] = useState<boolean>(false);
	const [showRemoveFlagDialog, setShowRemoveFlagDialog] = useState<boolean>(false);
	const [waitingReason, setWaitingReason] = useState<string | undefined>(undefined);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	const missingDeleteReasonMessage = "Please provide a reason for deleting this shipment.";

	useEffect(() => {
		if (shipmentDetails) {
			if (shipmentDetails?.customerClosedThroughDate) {
				// Earliest date is the day after the locked date				
				setEarliestAllowedDate(dayjs(shipmentDetails.customerClosedThroughDate).add(1, 'day'));
			}

			if (shipmentDetails?.waitingFor) {
				setWaitingReason(shipmentDetails?.waitingFor);
			}
		}
	}, [shipmentDetails])

	useEffect(() => {
		populateSelectedVendorTransporterIds();
	}, [selectedVendorId])

	useEffect(() => {
		if (!showVendorField) {
			setSelectedVendorId(shipmentDetails.vendorId);
			setSelectedTransporterId(shipmentDetails.transporterId);
		}
		if (!showTransporterField) {
			setSelectedTransporterId(shipmentDetails.transporterId);
		}
	}, [showVendorField, showTransporterField])

	const getAssociatedTransporterIds = (vendorId: string): number[] => {
		return vendorTransporterValueSets.vendors.find(v => v.vendorId.toString() == vendorId)?.associatedTransporterIds ?? [];
	}

	const isVendorInvalid = () => {
		if (!vendorTransporterValueSets) return false;
		return !vendorTransporterValueSets.vendors.find(v => v.vendorId.toString() == shipmentDetails.vendorId)?.isAssociatedToCustomer;
	}

	const isTransporterInvalid = () => {
		if (!vendorTransporterValueSets) return false;
		if (showVendorField) { // Warn when changing vendor if it invalidates the current transporter association
			return !getAssociatedTransporterIds(selectedVendorId).includes(+selectedTransporterId);
		}
		else { // Otherwise warn if the saved vendor and transporter are not associated
			return !getAssociatedTransporterIds(shipmentDetails.vendorId).includes(+shipmentDetails.transporterId);
		}
	}

	const getSelectedVendorName = () => {
		return vendorTransporterValueSets.vendors.find(v => v.vendorId.toString() == selectedVendorId)?.vendorName ?? `(Unknown Vendor ID ${selectedVendorId})`;
	}

	const handleDelete = () => {
		if (!deleteReason || deleteReason.trim().length == 0) {
			setErrorOnDelete(missingDeleteReasonMessage);
			return;
		}

		setDisableWorkflowButtons(true);
		setWorkingModalParams({ params: { content: "Deleting shipiment..." } });

		ShipmentService.deleteShipment(shipmentDetails.id.toString(), deleteReason)
			.then((response) => {
				console.log('ShipmentService.deleteShipment: SUCCESS', response)
				navigate("/shipment");
			})
			.catch((error) => {
				console.log('ShipmentService.deleteShipment: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setWorkingModalParams(undefined);
			});
	}

	const cancelDelete = () => {
		setShowRealDeleteButton(false);
		setErrorOnDelete(undefined);
	}

	const handleDateSave = () => {
		setErrorMessage(undefined);
		if (compareDates(dayjs(dateField), earliestAllowedDate)) {
			setDisableWorkflowButtons(true);

			ShipmentService.changeDate(shipmentDetails.id.toString(), dateField)
				.then((response) => {
					console.log('ShipmentService.changeDate: SUCCESS', response);
					var dateString = (moment(dateField.toString())).format('MM/DD/yyyy');
					setPickupDate(dateString);
				})
				.catch((error) => {
					console.log('ShipmentService.changeDate: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
					window.scrollTo(0, 0);
				})
				.finally(() => {
					setShowDateField(false);
					setDisableWorkflowButtons(false);
				});
		}
		else {
			setErrorMessage(`The pickup date must be on or after ${earliestAllowedDate.format('LL')}.`);
			window.scrollTo(0, 0);
		}
	}

	const compareDates = (firstDate: Dayjs, secondDate: Dayjs) => {
		const firstDateAsInt = moment(firstDate.toString()).format("YYYYMMDD");
		const secondDateAsInt = moment(secondDate.toString()).format("YYYYMMDD");

		if (firstDateAsInt >= secondDateAsInt) {
			return true;
		}

		return false;
	}

	const handleBolSave = () => {
		if (bolField.trim() == "") {
			setErrorMessage("BOL number cannot be empty.");
			return;
		}

		setErrorMessage(undefined);
		setDisableWorkflowButtons(true);

		ShipmentService.changeBol(shipmentDetails.id.toString(), bolField)
			.then((response) => {
				console.log('ShipmentService.changeBol: SUCCESS', response);
				shipmentDetails.hwiNumber = response.data;
				editBol(false);
			})
			.catch((error) => {
				console.log('ShipmentService.changeBol: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
			});
	}

	const editBol = (edit: boolean) => {
		if (edit) {
			setBolField(shipmentDetails.hwiNumber);
		}
		setShowBolField(edit);
	}

	const handleBolKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			handleBolSave();
		}
		if (event.key === 'Escape') {
			editBol(false);
		}
	}

	const populateSelectedVendorTransporterIds = () => {
		if (vendorTransporterValueSets) {

			let vendorId = "0";
			if (selectedVendorId == "0") { // If a vendor is not selected, try to find one equal to the shipment's vendorId
				vendorId = vendorTransporterValueSets.vendors.find(v => v.vendorId.toString() == shipmentDetails.vendorId)?.vendorId.toString() ?? "0";

				// If the vendorId is 0, we didn't find one, but since the selectedVendorId was already 0, we just leave it
				if (vendorId != "0") {
					setSelectedVendorId(vendorId);
				}
			}
			else {
				vendorId = selectedVendorId;
			}

			const associatedTransporterIds = getAssociatedTransporterIds(vendorId);
			// If a transporter is not selected, try to find one equal to the shipment's transporterId if the it's associated to the vendor
			if (selectedTransporterId == "0") {
				const transporterId = associatedTransporterIds.find(t => t.toString() == shipmentDetails.transporterId)?.toString() ?? "0";

				// If the transporterId is 0, we didn't find one, but since the selectedTransporterId was already 0, we just leave it
				if (transporterId != "0") {
					setSelectedTransporterId(transporterId);
				}
			}
			else {
				// If a transporter is already selected, ensure it's associated to the selected vendor
				if (!associatedTransporterIds.includes(+selectedTransporterId)) {
					setSelectedTransporterId("0");
				}
			}
		}
	}

	const handleSaveVendorAndTransporter = () => {
		setErrorMessage(undefined);
		const newVendorId = (showVendorField && !isTransporterInvalid() && selectedVendorId != "0" && selectedVendorId != shipmentDetails.vendorId) ? selectedVendorId : "0";
		const newTransporterId = (showTransporterField && selectedTransporterId != "0" && selectedTransporterId != shipmentDetails.transporterId) ? selectedTransporterId : "0";
		if (newVendorId == "0" && newTransporterId == "0") return;

		setIsSavingVendorAndTransporter(true);
		ShipmentService.changeVendorAndTransporterIds(shipmentDetails.id.toString(), newVendorId, newTransporterId)
			.then((response) => {
				console.log('ShipmentService.changeVendorAndTransporterIds: SUCCESS', response);
				if (newVendorId != "0") {
					var newVendor = vendorTransporterValueSets.vendors.find(v => v.vendorId.toString() == newVendorId);
					shipmentDetails.vendorId = newVendorId;
					shipmentDetails.vendorName = newVendor.vendorName ?? "(Unknown)"; // If we can't find the vendor or transporrer we just saved we're in trouble, but be safe anyway
				}
				if (newTransporterId != "0") {
					var newTransporter = vendorTransporterValueSets.transporters.find(t => t.transporterId.toString() == newTransporterId);
					shipmentDetails.transporterId = newTransporterId;
					shipmentDetails.transporterName = newTransporter.transporterName ?? "(Unknown)";
				}
				setShowVendorField(false);
				setShowTransporterField(false);
			})
			.catch((error) => {
				console.log('ShipmentService.changeVendorAndTransporterIds: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setIsSavingVendorAndTransporter(false);
			});
	}

	const editVendor = (edit: boolean) => {
		if (edit) {
			populateSelectedVendorTransporterIds();
		}
		setShowVendorField(edit);
	}


	const editTransporter = (edit: boolean) => {
		if (edit) {
			populateSelectedVendorTransporterIds();
		}
		setShowTransporterField(edit);
	}

	const handleFlagShipment = () => {
		ShipmentService.addFlag(shipmentDetails.id, waitingReason)
			.then((response) => {
				console.log('ShipmentService.addFlag: SUCCESS', response);
				setWaitingReason(waitingReason);
			})
			.catch((error) => {
				console.log('ShipmentService.addFlag: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setShowAddFlagDialog(false);
			});
	}

	const handleUnflagShipment = () => {
		ShipmentService.removeFlag(shipmentDetails.id)
			.then((response) => {
				console.log('ShipmentService.removeFlag: SUCCESS', response);
				setWaitingReason(undefined);
			})
			.catch((error) => {
				console.log('ShipmentService.removeFlag: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setShowRemoveFlagDialog(false);
			});
	}

	return (
		<section className="hwi-shipment-details-form">
			<Spinner dependency={isLoadingDetails || isLoadingVendorsAndTransporters || isLoadingShipmentManifestWarnings} text="Loading details...">
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					{waitingReason &&
						<Alert className="alert-error"
							severity="error"
						>
							<strong>Waiting: </strong>{shipmentDetails?.waitingFor ? shipmentDetails?.waitingFor : waitingReason}
						</Alert>
					}
					{errorMessage &&
						<Alert className="alert-success"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{errorMessage}
						</Alert>
					}
					{errorOnDelete &&
						<Alert className="alert"
							severity="error"
							action={<IconButton onClick={() => { setErrorOnDelete(undefined); }} size='small'>X</IconButton>}>
							{errorOnDelete}
						</Alert>
					}
					{warningMessage &&
						<Alert className="alert-warning"
							severity="warning"
							action={<IconButton onClick={() => { setWarningMessage(undefined); }} size='small'>X</IconButton>}>
							{warningMessage}
						</Alert>
					}
					<Form>
						<div className="row">
							<p className="label">BOL #</p>
							{showBolField ? (
								<p className="value">
									<TextField
										className="field-bol"
										autoFocus={true}
										name="value"
										placeholder={`(was ${shipmentDetails.hwiNumber})`}
										required
										value={bolField}
										size="small"
										disabled={disableWorkflowButtons}
										onFocus={(event => event.target.select())}
										onChange={(event) => { setBolField(event.target.value.substring(0, 50)) }}
										onKeyDown={(event) => handleBolKeyDown(event)}>
									</TextField>
									<Button className='for-text' variant="contained" color='primary' disabled={disableWorkflowButtons} onClick={() => handleBolSave()}>Update</Button>
									{" "}
									<Button className='for-text' variant="contained" color='error' disabled={disableWorkflowButtons} onClick={() => editBol(false)}>Cancel</Button>
								</p>
							) : (
								<p className="value">
									{shipmentDetails.hwiNumber + " "}
									{shipmentDetails.isLegacy &&
										<Icons.LegacyData fill="#A84825" height="12px"></Icons.LegacyData>
									}
									{" "}
									{shipmentDetails.status.toLowerCase() != "complete" &&
										<Button variant="outlined" color='primary' className="consistent-text-height" size='small' disabled={disableWorkflowButtons} onClick={() => editBol(true)}>Update BOL#</Button>
									}
								</p>
							)}
						</div>
						<div className="row">
							<p className="label">Manifest #</p>
							<p className="value">
								{shipmentDetails?.manifestNumbers?.length > 0 ? (
									<div className="manifest-container">
										{shipmentDetails.manifestNumbers.map((mn, index) => (
											<React.Fragment key={index}><span className="manifest-item">{mn}</span>{index < shipmentDetails.manifestNumbers.length - 1 ? ", " : ""}</React.Fragment>
										))
										}
									</div>
								) : (
									<span className='unavailable'>No manifest numbers have been provided for this shipment.</span>
								)}
							</p>
						</div>
						{shipmentDetails.batchId ? (
							<>
								<div className="row">
									<p className="label">Batch #</p>
									<p className="value">
										<NavLink to={"/batch/" + shipmentDetails.batchId}>
											<Button>
												<Icons.ViewItem fill="#434D76" height="16px"></Icons.ViewItem> <span>Batch #{shipmentDetails.batchId}</span>
											</Button>
										</NavLink>
									</p>
								</div>
								<div className="row">
									<p className="label">Batch Status</p>
									<p className="value">{shipmentDetails.batchStatus}</p>
								</div>
							</>
						) : (
							<div className="row">
								<p className="label">Status</p>
								<p className="value">{shipmentDetails.status}</p>
							</div>
						)}
						<div className="row">
							<p className="label">Customer</p>
							<p className="value">{shipmentDetails.customerName}</p>
						</div>
						<div className="row">
							<p className="label">
								Vendor
								{isLoadingVendorsAndTransporters ? " (Loading...)" : ""}
								{isSavingVendorAndTransporter && showVendorField ? " (Saving...)" : ""}
							</p>
							<p className='value'>
								{showVendorField ? (
									<>
										<Select
											className="field-vendor"
											name="value"
											required
											value={selectedVendorId}
											size="small"
											disabled={disableWorkflowButtons || isLoadingVendorsAndTransporters || isSavingVendorAndTransporter || (!isLoadingVendorsAndTransporters && vendorTransporterValueSets?.vendors.length == 0)}
											onChange={(event) => { setSelectedVendorId(event.target.value) }}>

											{isLoadingVendorsAndTransporters &&
												<MenuItem key={-1} value="0">Loading Vendors...</MenuItem>
											}
											{!isLoadingVendorsAndTransporters &&
												vendorTransporterValueSets?.vendors?.length > 0 ? (
												selectedVendorId == "0" &&
												<MenuItem key={-1} value="0">Select a Vendor</MenuItem>

											) : (
												<MenuItem key={-1} value="0">Customer has no available Vendors</MenuItem>
											)
											}
											{vendorTransporterValueSets?.vendors.filter(v => v.isAssociatedToCustomer).sort((a, b) => UtilityService.compareStrings(a.vendorName, b.vendorName)).map((vendor, index) => (
												<MenuItem key={index} value={vendor.vendorId.toString()}>{vendor.vendorName}{!vendor.isActive && " (Inactive)"}</MenuItem>
											))}
										</Select>
										<Button className='for-select' variant="contained" color='primary' disabled={disableWorkflowButtons || isSavingVendorAndTransporter || isTransporterInvalid()} onClick={() => handleSaveVendorAndTransporter()}>Update</Button>
										{" "}
										<Button className='for-select' variant="contained" color='error' disabled={disableWorkflowButtons || isSavingVendorAndTransporter} onClick={() => editVendor(false)}>Cancel</Button>
										{" "}
									</>
								) : (
									<>
										{shipmentDetails.vendorName}
										{" "}
										{userContext.permissions?.canChangeShipmentVendor && shipmentDetails.status.toLowerCase() != "complete" &&
											<Button variant="outlined" color='primary' className="consistent-text-height" size='small' disabled={disableWorkflowButtons || isSavingVendorAndTransporter} onClick={() => editVendor(true)}>Update Vendor</Button>
										}
									</>
								)}
								{isVendorInvalid() && <div className="comment-error">* Vendor <strong>{shipmentDetails.vendorName}</strong> is not associated with the customer. Please choose another vendor.</div>}
								{isTransporterInvalid() && showVendorField && <div className="comment-error">* Cannot update vendor to <strong>{getSelectedVendorName()}</strong> with an invalid transporter association.</div>}
							</p>
						</div>
						<div className="row">
							<p className="label">
								Transporter
								{isLoadingVendorsAndTransporters ? " (Loading...)" : ""}
								{isSavingVendorAndTransporter && showTransporterField ? " (Saving...)" : ""}
							</p>
							<p className="value">
								{showTransporterField ? (
									<>
										<Select
											className={`field-transporter${selectedTransporterId == "0" ? " error" : ""}`}
											name="value"
											required
											value={selectedTransporterId}
											size="small"
											disabled={disableWorkflowButtons || isLoadingVendorsAndTransporters || isSavingVendorAndTransporter || (!isLoadingVendorsAndTransporters && vendorTransporterValueSets?.transporters.length == 0)}
											onChange={(event) => { setSelectedTransporterId(event.target.value) }}>

											{isLoadingVendorsAndTransporters &&
												<MenuItem key={-1} value="0">Loading Transporters...</MenuItem>
											}
											{!isLoadingVendorsAndTransporters &&
												vendorTransporterValueSets?.vendors?.length > 0 ? (
												selectedTransporterId == "0" &&
												<MenuItem key={-1} value="0">Select a Transporter</MenuItem>

											) : (
												<MenuItem key={-1} value="0">Vendor has no available Transporters</MenuItem>
											)
											}
											{vendorTransporterValueSets?.transporters
												.filter(t => {
													return getAssociatedTransporterIds(showVendorField ? selectedVendorId : shipmentDetails.vendorId.toString()).includes(t.transporterId);
												})
												.map((transporter, index) => (
													<MenuItem key={index} value={transporter.transporterId.toString()}>{transporter.transporterName}{!transporter.isActive && " (Inactive)"}</MenuItem>
												))
											}
										</Select>
										<Button className='for-select' variant="contained" color='primary' disabled={disableWorkflowButtons || isSavingVendorAndTransporter || selectedTransporterId == "0"} onClick={() => handleSaveVendorAndTransporter()}>Update</Button>
										{" "}
										<Button className='for-select' variant="contained" color='error' disabled={disableWorkflowButtons || isSavingVendorAndTransporter} onClick={() => editTransporter(false)}>Cancel</Button>
									</>
								) : (
									<>
										{shipmentDetails.transporterName}
										{" "}
										{userContext.permissions?.canChangeShipmentVendor && shipmentDetails.status.toLowerCase() != "complete" &&
											<Button variant="outlined" color='primary' className="consistent-text-height" size='small' disabled={disableWorkflowButtons || isSavingVendorAndTransporter} onClick={() => editTransporter(true)}>Update Transporter</Button>
										}
									</>
								)}
								{isTransporterInvalid() && <div className="comment-error">* Transporter <strong>{shipmentDetails.transporterName}</strong> is not associated with vendor <strong>{showVendorField ? getSelectedVendorName() : shipmentDetails.vendorName}</strong>. Please choose another transporter.</div>}
							</p>
						</div>
						<div className="row">
							<p className="label">Plant Weight</p>
							<p className="value">{FormatService.weight(shipmentDetails.plantWeight)} pounds</p>
						</div>
						<div className="row">
							<p className="label">Vendor Weight</p>
							<p className="value">{FormatService.weight(shipmentDetails.vendorWeight)} pounds</p>
						</div>
						<div className="row">
							<p className="label">Request Date</p>
							<p className="value">{shipmentDetails.scheduledPickupDate ? moment(shipmentDetails.scheduledPickupDate).format("MM/DD/yyyy") : <span className='unavailable'>N/A</span>}</p>
						</div>
						<div className="row">
							<p className="label">Pickup Date</p>
							<p className="value">
								{showDateField ? (
									<>
										<DatePicker className="start-date-field" value={dayjs(dateField)} minDate={earliestAllowedDate} onChange={(value) => setDateField(value.toDate())} />
										{" "}
										<Button className='for-text' variant="contained" color='primary' disabled={disableWorkflowButtons} onClick={() => handleDateSave()}>Save Date</Button>
										{" "}
										<Button className='for-text' variant="contained" color='error' disabled={disableWorkflowButtons} onClick={() => setShowDateField(false)}>Cancel</Button>
									</>
								) : (
									<>
										{pickupDate}
										{shipmentDetails.status.toLowerCase() != "complete" && ((vendor && !vendor.details?.requiresEscalatedDateChangePermission) || userContext.permissions?.canChangeShipmentDatesForVendorsRequiringEscalatedPermission) &&
											<Button variant="outlined" color='primary' size='small' disabled={disableWorkflowButtons} onClick={() => setShowDateField(true)}>Change Date</Button>
										}
									</>
								)}
							</p>
						</div>
						<div className="row">
							<p className="label">TILS Load ID</p>
							<p className="value">
								{shipmentDetails.loadId ? (
									<NavLink to={"/tils/" + shipmentDetails.loadId}>
										<Button>
											<Icons.ViewItem fill="#434D76" height="16px"></Icons.ViewItem>
											<span>{"   " + shipmentDetails.loadId}</span>
										</Button>
									</NavLink>
								) : (
									<span>NA</span>
								)}
							</p>
						</div>
						<div className="row text-right">
							{!showRealDeleteButton && (
								waitingReason ? (
									<Button className="btn-add-flag" variant="contained" color='primary' onClick={() => setShowRemoveFlagDialog(true)}>Remove Waiting Flag</Button>
								) : (
									<Button className="btn-add-flag" variant="contained" color='primary' onClick={() => setShowAddFlagDialog(true)}>Flag Shipment</Button>
								)

							)}
							{((userContext.permissions?.canDeleteShipments && shipmentDetails.status.toLowerCase() != "complete") || userContext.permissions?.canUnlockFinalizedShipments) &&
								<>
									{showRealDeleteButton ? (
										<>
											{shipmentDetails.batchStatus ? (
												<div className="delete-actions">
													<span>This shipment is in a batch and may not be deleted.</span>
													&nbsp;&nbsp;
													<Button variant="contained" color='primary' onClick={() => setShowRealDeleteButton(false)}>Dismiss</Button>
												</div>
											) : (
												<div className="delete-actions">
													<span>Are you sure you want to delete this shipment?</span>
													<TextField
														className={`deleted-reason-field ${errorOnDelete == missingDeleteReasonMessage ? "error" : ""}`}
														placeholder="Reason for deleting (Required)"
														disabled={disableWorkflowButtons}
														name="deletedReason"
														value={deleteReason ?? ""}
														onChange={(e) => setDeleteReason(e.target.value)}
														size="small"
													></TextField>
													<Button variant="contained" color='primary' disabled={disableWorkflowButtons} onClick={handleDelete}>Yes: Delete</Button>
													<Button variant="contained" color='error' disabled={disableWorkflowButtons} onClick={cancelDelete}>No: Cancel</Button>
												</div>
											)}
										</>
									) : (
										<Button variant="contained" color='error' disabled={disableWorkflowButtons} onClick={() => setShowRealDeleteButton(true)}>Delete Shipment</Button>
									)}
								</>
							}
						</div>
					</Form>
					<Dialog
						open={showAddFlagDialog}
						aria-labelledby='add-flag-confirmation-title'
						className='add-flag-confirmation-dialog'
						maxWidth='sm'
						fullWidth={true}>
						<DialogTitle id="add-flag-confirmation-title">
							{"Add Waiting Flag"}
						</DialogTitle>
						<DialogContent>
							<DialogContentText>
								<p>What is this shipment waiting on?</p>
								<TextField
									className="waiting-reason"
									autoFocus={true}
									name="value"
									value={waitingReason}
									size="small"
									fullWidth
									multiline={true}
									rows={3}
									onChange={(event) => { setWaitingReason(event.target.value) }}
								>
								</TextField>
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button color="primary" variant="contained" disabled={!waitingReason} onClick={() => handleFlagShipment()}>Add Flag</Button>
							<Button color="error" variant='outlined' onClick={() => setShowAddFlagDialog(false)}>Cancel</Button>
						</DialogActions>
					</Dialog>

					<Dialog
						open={showRemoveFlagDialog}
						aria-labelledby='remove-flag-confirmation-title'
						className='remove-flag-confirmation-dialog'
						maxWidth='sm'
						fullWidth={true}>
						<DialogTitle id="remove-flag-confirmation-title">
							{"Remove Waiting Flag"}
						</DialogTitle>
						<DialogContent>
							<DialogContentText>
								<p>Are you sure you want to remove the flag on this shipment?</p>
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button color="primary" variant="contained" disabled={!waitingReason} onClick={() => handleUnflagShipment()}>Remove Flag</Button>
							<Button color="error" variant='outlined' onClick={() => setShowRemoveFlagDialog(false)}>Cancel</Button>
						</DialogActions>
					</Dialog>
				</LocalizationProvider>
				{/* <Json data={userContext.permissions}></Json>
				<Json data={vendor}></Json> */}
			</Spinner>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</section>
	)
}

export default ShipmentDetailsForm;